
import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import { LogIn, AlertTriangle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { 
  Dialog, 
  DialogContent, 
  DialogTrigger 
} from "@/components/ui/dialog";
import OtpVerification from "@/components/auth/OtpVerification";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const { signIn, signInWithProvider } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get return URL from location state if available
  const returnTo = location.state?.returnTo || "/entries";
  
  // Track consecutive login failures for enhanced security
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [requireOtp, setRequireOtp] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: t("common.error", language),
        description: t("login.missingCredentials", language) || "Please fill in both email and password fields",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // If we've had multiple failures, require OTP verification
      if (failedAttempts >= 3) {
        setRequireOtp(true);
        setShowOtpVerification(true);
        // Send OTP email before showing the verification dialog
        await supabase.auth.signInWithOtp({
          email,
          options: {
            shouldCreateUser: false,
          }
        });
        setIsLoading(false);
        return;
      }
      
      await signIn(email, password);
      
      // Reset failed attempts counter on success
      setFailedAttempts(0);
      
      // Navigate to the return URL or default to entries
      navigate(returnTo);
    } catch (error) {
      console.error("Login error:", error);
      
      // Increment failed attempts
      setFailedAttempts(prev => prev + 1);
      
      // Show appropriate error message
      if (failedAttempts >= 2) {
        toast({
          title: t("login.errorTitle", language) || "Login failed",
          description: "Too many failed attempts. Next login will require email verification.",
          variant: "destructive",
        });
      } else {
        toast({
          title: t("login.errorTitle", language) || "Login failed",
          description: error instanceof Error ? error.message : t("login.errorMessage", language),
          variant: "destructive",
        });
      }
    } finally {
      if (!requireOtp) {
        setIsLoading(false);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithProvider('google');
    } catch (error) {
      console.error("Google login error:", error);
      toast({
        title: t("login.googleErrorTitle", language) || "Google sign-in failed",
        description: error instanceof Error ? error.message : t("login.errorMessage", language),
        variant: "destructive",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleOtpSuccess = () => {
    setShowOtpVerification(false);
    setRequireOtp(false);
    setFailedAttempts(0);
    navigate(returnTo);
  };

  return (
    <main className="min-h-screen pt-24 pb-16">
      <Header />
      <div className="container max-w-md mx-auto px-4">
        <div className="mb-8 text-center animate-fade-down">
          <LogIn className="mx-auto h-12 w-12 text-primary mb-4" />
          <h1 className="text-3xl font-bold tracking-tight">{t('login.title', language)}</h1>
          <p className="text-muted-foreground mt-1">
            {t('login.subtitle', language)}
          </p>
        </div>

        {failedAttempts > 0 && (
          <div className="mb-6 p-3 border border-yellow-200 bg-yellow-50 rounded flex items-center text-sm text-yellow-800">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
            <p>
              {failedAttempts === 1 
                ? "Invalid credentials. Please try again." 
                : `${3 - failedAttempts} attempts remaining before verification is required.`}
            </p>
          </div>
        )}

        <Button 
          variant="outline" 
          className="w-full flex items-center justify-center gap-2 mb-4"
          onClick={handleGoogleSignIn}
          disabled={isGoogleLoading}
        >
          {isGoogleLoading ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
            </svg>
          )}
          {isGoogleLoading ? t('login.signingIn', language) || "Signing in..." : t('auth.googleLogin', language)}
        </Button>

        <div className="relative my-6">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
            {t('login.or', language) || "OR"}
          </span>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('login.form.email', language)}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('login.emailPlaceholder', language) || "you@example.com"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="bg-white"
              autoComplete="username"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t('login.form.password', language)}</Label>
              <Link 
                to="/forgot-password" 
                className="text-sm font-medium text-primary hover:underline"
              >
                {t('login.form.forgotPassword', language)}
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="bg-white"
              autoComplete="current-password"
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 
              (t('login.signingIn', language) || "Signing in...") : 
              (t('login.form.login', language) || "Sign in with Email")
            }
          </Button>
          
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground">
              {t('login.noAccount', language)}{" "}
              <Link 
                to="/signup" 
                className="font-medium text-primary hover:underline"
              >
                {t('login.createOne', language)}
              </Link>
            </p>
          </div>
        </form>
      </div>

      {/* OTP Verification Dialog */}
      <Dialog open={showOtpVerification} onOpenChange={setShowOtpVerification}>
        <DialogContent className="sm:max-w-md">
          <OtpVerification 
            email={email} 
            onSuccess={handleOtpSuccess}
            onCancel={() => {
              setShowOtpVerification(false);
              setIsLoading(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Login;
